import React, { useEffect, useState } from 'react';
import { FC } from 'react';
import { Icons } from '@components/Icon';
import useTranslations from '@hooks/useTranslation';
import { secondsToHMS } from '@utils/time.utils';
import { COMPONENT_TYPE, IColors, IConferenceItem } from '../../interfaces/Conference';
import {
    Author,
    Chapter,
    ChapterIcon,
    ChapterTime,
    ChapterTitle,
    Chapters,
    Content,
    InfoMessage,
    Tab,
    Tabs,
    Title,
    TitleSymbol,
    Wrapper,
} from './ConferenceVideo.elements';

interface ConferenceProps {
    colors?: IColors;
    conf?: IConferenceItem;
}

const ConferenceVideoTemplate: FC<ConferenceProps> = ({ colors, conf }) => {
    const { t } = useTranslations();

    const [activeSection, setActiveSection] = useState('videosTab0');
    const [activeChapter, setActiveChapter] = useState('');
    const [videos, setVideos] = useState([]);
    const [termAccessible, setTermAccessible] = useState(false);

    useEffect(() => {
        if (conf) {
            for (const date of conf?.dates) {
                if (date.accessible) {
                    for (const video of date.videos) {
                        videos.push(video);
                    }
                }
            }
            const videosUnique = videos.filter(
                (value, index, self) => self.map((x) => x._key).indexOf(value._key) == index
            );
            setVideos(videosUnique);

            for (const date of conf?.dates) {
                if (date.accessible) {
                    setTermAccessible(true);
                    break;
                }
            }
        }
    }, [conf]);

    const handleClick = function (e, time, index, index2) {
        const videoElement = document.getElementById(`recordVideo${index}`);
        (videoElement as HTMLVideoElement).currentTime = time;
        setActiveChapter(`chapter${index}${index2}`);
        e.preventDefault();
    };

    return (
        <Wrapper id={`confSection${COMPONENT_TYPE.VIDEO}`} colors={colors}>
            <Title colors={colors}>
                {t('conference.component.record.title')}
                <TitleSymbol colors={colors} />
            </Title>

            {videos.length > 0 ? (
                <>
                    {videos?.length > 0 && (
                        <>
                            <Tabs>
                                {videos?.map((item, index) => (
                                    <>
                                        <Tab
                                            colors={colors}
                                            className={activeSection === `videosTab${index}` ? 'active' : ''}
                                            onClick={() => setActiveSection(`videosTab${index}`)}
                                        >
                                            {index + 1}. {t('conference.general.record')}
                                        </Tab>
                                    </>
                                ))}
                            </Tabs>
                        </>
                    )}

                    {videos?.map((item, index) => (
                        <>
                            {activeSection === `videosTab${index}` && (
                                <Content>
                                    <>
                                        {item?.url && (
                                            <video width="100%" controls id={`recordVideo${index}`}>
                                                <source src={item?.url} type="video/mp4" />
                                            </video>
                                        )}

                                        <Author>{t('conference.general.sourcePP')}</Author>
                                        <Chapters>
                                            {item?.chapters?.map((item2, index2) => (
                                                <Chapter
                                                    key={`chapterKey${index}${index2}`}
                                                    onClick={(e) => handleClick(e, item2.time, index, index2)}
                                                >
                                                    <ChapterTime
                                                        colors={colors}
                                                        isActive={activeChapter === `chapter${index}${index2}`}
                                                    >
                                                        {secondsToHMS(item2.time)}
                                                    </ChapterTime>
                                                    <ChapterIcon
                                                        name={Icons.Play}
                                                        colors={colors}
                                                        isActive={activeChapter === `chapter${index}${index2}`}
                                                    />
                                                    <ChapterTitle
                                                        colors={colors}
                                                        isActive={activeChapter === `chapter${index}${index2}`}
                                                    >
                                                        {index2 + 1}. {item2.title}
                                                    </ChapterTitle>
                                                </Chapter>
                                            ))}
                                        </Chapters>
                                    </>
                                </Content>
                            )}
                        </>
                    ))}
                </>
            ) : (
                <Content>
                    {termAccessible && videos.length === 0 && (
                        <InfoMessage>{t('conference.component.record.anyRecords.accessible')}</InfoMessage>
                    )}
                    {!termAccessible && videos.length === 0 && (
                        <InfoMessage>{t('conference.component.record.anyRecords')}</InfoMessage>
                    )}
                </Content>
            )}
        </Wrapper>
    );
};

export default ConferenceVideoTemplate;
